import { useState, useRef, useEffect, useMemo } from "react";
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, Menu, Popover, TextField, InputAdornment, FormControlLabel, Button, Tooltip, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AgGridReact } from "ag-grid-react";
import { withTranslation } from "react-i18next";
import debug from "@plannel/utils/debug-util";
import {isEqual, uniq, values, keys, includes, isEmpty, isNil, cloneDeep, size} from "lodash";
import SettingsIcon from "@mui/icons-material/Settings";
import SaveIcon from "@mui/icons-material/Save";
import planConfigService from "@plannel/services/data/plan-config-service";
import measureService from "@plannel/services/data/measure-service";
import planVersionService from "@plannel/services/data/plan-version-service";
import userService from "@plannel/services/system/user-service";
import MeasureNameSettings from "@plannel/components/MeasureNameSettings";

import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";
import { CalculationButton, DetailButton } from "@plannel/components/ActionIconButton";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import DataState from "@plannel/components/aggrid/DataState";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import codeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import ComputeMeasureModal from "../dp-workbench/dp-workbench-modal/ComputeMeasureModal";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import axiosUtil from "@plannel/utils/axios-bigint";
import WorkbenchUtil from "../dp-workbench/WorkbenchUtil";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import DpMeasureAlertModal from "../dp-modal/DpMeasureAlertModal";
import DpInitialSettingModal from "../dp-modal/DpInitialSettingModal";
import ExpandMore from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ResetIcon from "@mui/icons-material/RotateLeft";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import SegmentedControl from "@plannel/components/ui/SegmentedControl";
import AppCard from "@plannel/components/ui/AppCard";
import StatPill from "@plannel/components/ui/StatPill";
import {
  MeasureActiveCell,
  MeasureComputeCell,
  MeasureTypeCell,
  MeasureTextCell,
  MeasureListCell,
} from "@plannel/components/aggrid/MeasureGridCells";
import MeasureDetailPanel from "./MeasureDetailPanel";
import { FS } from "@plannel/utils/font-util";
import "@plannel/scss/components/MeasureSheetGrid.scss";

// Workbench Measure 시트의 표시 범위 세그먼트
const ALL_MEASURES = "ALL";
const ACTIVE_ONLY = "ACTIVE";
const MEASURE_DISPLAY_NAMES_CONFIG_CD = "MEASURE_DISPLAY_NAMES";
/*
 * 채널 전용 측정치(MeasureType.java 의 channelAllMeasure 중, 다른 화면에서 재사용되는 일반 측정치는
 * 빼고 채널에서만 의미가 있는 것만) — BF Source Data(POS_ACTUAL_FLG)가 꺼져 있으면 POS 계열 데이터를
 * 안 쓰기로 한 것이므로 이 화면(Workbench Measure 시트)에서 전부 숨긴다.
 * 이 중 다수는 codeUtil.dpMeasure 에 항목이 없다(수식 안에서만 조립되는 파생 측정치라 화면 코드가
 * 직접 참조할 일이 없었음) — 그래서 measureCd 문자열로 직접 나열한다.
 */
const CHANNEL_ONLY_MEASURE_CDS = codeUtil.channelOnlyMeasureCds;
// 컴포넌트 밖에 고정 — 렌더마다 새 객체를 만들면 memo(MeasureNameSettings)의 참조 비교가 매번 깨진다.
const MEASURE_NAME_SETTINGS_SX = { mb: 1.5, maxWidth: 1720 };
/*
 * option 칸 툴팁 — 행에는 "어떤 안내인지"만 키로 담고 글자는 띄울 때 만든다.
 * 번역문을 행 데이터에 굳히면 언어를 바꿔도 state 가 그대로라 옛 언어가 남는다.
 * 글자를 만드는 시점이 그리드 콜백 안이라 컴포넌트의 t 대신 언어 번들을 그때 읽는 gridValueL10N 을 쓴다
 * (컬럼 정의는 setColumnDefs 시점의 t 를 가둬 두므로 언어를 바꿔도 옛 t 를 부른다).
 */
const OPTION_HELP = {
  RELEASE_VERSION: ["releaseVersion", "MSG_ReleaseVersion"],
  DISAGG_TARGET: ["disaggregationTarget", "MSG_DisaggregationTarget"],
};

const optionHelpText = (cd) => {
  const keys = OPTION_HELP[cd];
  return keys ? `${GridUtils.gridValueL10N(keys[0])}:${GridUtils.gridValueL10N(keys[1])}` : "";
};

/*
 * Workbench Measure 의 두 칸(목록 | 편집 패널)이 못 줄어드는 선.
 *
 * 예전에는 `minmax(0, 1080px) minmax(0, 1fr)` 이라 남는 폭이 줄면 오른쪽 편집 패널이
 * 0 까지 밀려 사라졌다(실측: 본문 폭 1244 일 때 트랙이 `1080px 164px`).
 * 이 화면이 좁아지는 건 창 크기 때문이 아니라 **옆에 뜬 패널들**(사이드바 펼침·워크플로우·AI) 때문인데,
 * md 브레이크포인트는 뷰포트를 보므로 한 칸 배치로 되돌아가 주지도 않는다.
 * 그래서 두 칸 다 최소 폭을 정하고, 그보다 좁아지면 바깥 스크롤러가 가로로 굴리게 둔다
 * (칸이 사라지는 것보다 가로 스크롤이 낫다).
 */
const MEASURE_GRID_MIN_WIDTH = 360;    // ACTIVE(76) + MEASURE(minWidth 260) + 여백
const MEASURE_DETAIL_MIN_WIDTH = 340;  // 라벨과 입력칸이 한 줄에 들어가는 폭

// 시트 툴바 버튼 — 검색창(30px 알약)과 같은 높이·모서리로 한 줄에 맞춘다
const TOOLBAR_BUTTON_SX = {
  height: 30,
  px: 1.5,
  borderRadius: 999,
  fontSize: FS.SUB,
  textTransform: "none",
  whiteSpace: "nowrap",
};

const DpSettings = ({ t, viewName }) => {
  const gridRef = useRef();
  const { dpMeasureType, dpAggRules, dpDisAggRules, dpDisAggTarget, dpInitalValues, dpSettingCode, dpConfig, buckets, module, dayOfWeek: dayOfWeeks, dpMeasure, nonRuleMeasureCds, aggEditableMeasureCds, roles, dpVersion } = codeUtil;
  const { RELEASED } = dpVersion;
  const dpBasicMeasureCds = keys(dpMeasure);
  const valueType = useRef(values(dpMeasureType));
  const aggRules = [dpAggRules.SUM];//NONE HIDE
  const defaultRule = [dpDisAggRules.EQUAL, dpDisAggRules.QTY_EQUAL, dpDisAggRules.COPY, dpDisAggRules.PROPORTION, dpDisAggRules.ITEM_RATIO, dpDisAggRules.CUST_RATIO, dpDisAggRules.ITEM_CUST, dpDisAggRules.CUST_ITEM];
  const disaggRules = [...defaultRule, dpDisAggRules.NONE];
  const disaggTarget = values(dpDisAggTarget);
  const USER_MEASURE = codeUtil.USER_MEASURE;
  const { saveSuccess, saveFail, noSaveData, validation } = ColorUtil.snackBarMessageColor;
  const { MONTH, PARTIAL_WEEK } = codeUtil.buckets;

  const { BUCKET_UNIT, PLANNING_RANGE, DTF, START_DAY, RUN_BF, INITIAL_DEMAND, PLAN_ITEM, HISTORY_SALES_RANGE, LR, DISAGGREGATION_TARGET, DEMAND_NETTING, APPLY_SNAPSHOT, NOTE_FLG, AVG_FLG, POS_ACTUAL_FLG } = dpConfig;
  const mandatorySave = [BUCKET_UNIT, PLANNING_RANGE, DTF, START_DAY, RUN_BF, INITIAL_DEMAND, PLAN_ITEM, HISTORY_SALES_RANGE, DEMAND_NETTING, APPLY_SNAPSHOT, NOTE_FLG, AVG_FLG, POS_ACTUAL_FLG];
  const { LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, NOTINITIAL, FINANCE_PLAN, MEASURE_CUSTOM, CUSTOMIZATION } = dpInitalValues;
  const { BASELINE_FORECAST, ITEM_LEVEL_ID, CUST_LEVEL_ID, CUST_LEVEL, ITEM_LEVEL, ROLLING_RANGE } = dpSettingCode;
  
  const UPDATED = "updated";
  const QTY = "QTY";
  const AMT = "AMT";
  const hasBaselineForecastSource = list => (list ?? []).some(compute => Object.values(compute).some(expr => expr?.includes(`[${codeUtil.dpMeasure.BF_QTY.cd}]`)));

  const { customer: CUSTOMER, item: ITEM } = codeUtil;
  const financePlanMeasures = [dpMeasure.FF_AMT.cd, dpMeasure.FF_QTY.cd];
  const salesPlanMeasures = [dpMeasure.SALES_PLAN_AMT.cd, dpMeasure.SALES_PLAN_QTY.cd];
  const releasedMeasures = [dpMeasure.LR_AMT.cd, dpMeasure.LR_QTY.cd];
  const avgMeasures = [dpMeasure.ASA_QTY.cd, dpMeasure.ASA_AMT.cd];
  const posAvgMeasures = [dpMeasure.POS_ASA_QTY.cd];

  const [alertSetOpen, setAlertSetOpen] = useState(false);
  const [idMap, setIdMap] = useState({});
  const [dtf, setDTF] = useState(0);
  const [dtfError, setDtfError] = useState(false);
  const [runBf, setRunBf] = useState(false);
  const [isDpNetting, setIsDpNetting] = useState(false);
  const [dpNettingUnit, setDpNettingUnit] = useState(QTY);
  const [isSetConfig, setIsSetConfig] = useState(false);
  const { DP } = module;

  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));
  const [userRoles, setUserRoles] = useState([]);
  const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION];
  // [다음 릴리즈 배포] Measure Customization — 이번 릴리즈 제외
  // const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, MEASURE_CUSTOM, CUSTOMIZATION];
  const initialValues = includes(userRoles, roles.APP_BF) ? [BASELINE_FORECAST, ...demandValList] : demandValList;

  const [bucketUnit, setBucketUnit] = useState(MONTH);
  const timeDimensions = useRef(Object.values(buckets));
  timeDimensions.current = timeDimensions.current.filter(dimension => dimension !== buckets.DAY);
  const [defaultValue, setDefaultValue] = useState(NOTINITIAL);
  const dayOfWeek = values(dayOfWeeks);
  const [startDay, setStartDay] = useState(dayOfWeeks?.MONDAY?.cd);
  const [planRange, setPlanRange] = useState(12);
  const [rollingRange, setRollingRange] = useState(0);
  const [rollingError, setRollingError] = useState(false);
  const [planItem, setPlanItem] = useState(false);
  const [computeMeasureList, setComputeMeasureList] = useState([]);
  const [customInitialData, setCustomInitialData] = useState({});

  const [measureData, setMeasureData] = useState([]);
  const [measureType, setMeasureType] = useState([]);
  const [oriMeasureList, setOriMeasureList] = useState([]);
  const [initialMeasureList, setInitialMeasureList] = useState([]);
  const [initialTarget, setInitialTarget] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  // Workbench Measure 시트 — 검색어 / Active only 필터 / 표시 건수
  const [measureKeyword, setMeasureKeyword] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [measureCounts, setMeasureCounts] = useState({ shown: 0, total: 0, active: 0 });
  // Measure 표시명(별칭) 설정 — IpSettings.js/RpSettings.js 의 MeasureNameSettings 와 동일한 패턴.
  // DP는 measure 가 훨씬 많고 늘어나므로(codeUtil.dpMeasure 고정 목록 대신) 백엔드
  // MeasureType.getDisplayNameList() 를 그대로 내려받아 쓴다.
  const [measureDisplayNames, setMeasureDisplayNames] = useState({});
  const [dpDisplayNameMeasureList, setDpDisplayNameMeasureList] = useState([]);
  const [dpDisplayNameMeasuresRaw, setDpDisplayNameMeasuresRaw] = useState([]);
  const [dpMeasureValueTypeMap, setDpMeasureValueTypeMap] = useState({});
  const measureDisplayNameConfig = useRef({ moduleCd: codeUtil.module.DP, configCd: MEASURE_DISPLAY_NAMES_CONFIG_CD, activeFlg: true });

  // 오른쪽 패널이 편집 중인 행. 행 데이터는 제자리에서 바뀌므로(AG Grid), 화면에 그릴 값은 사본으로 들고 있는다.
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [selectedRowSnapshot, setSelectedRowSnapshot] = useState(null);
  const selectedNodeRef = useRef(null);
  // 수식 칸에 끼워 넣을 조각 — "[" 로 연 measure 메뉴에서 고른 값
  const [formulaInsert, setFormulaInsert] = useState(null);
  const formulaInsertSeq = useRef(0);
  // AG Grid 가 필터 콜백을 붙잡아두므로 최신 값은 ref 로 읽는다.
  const measureFilterRef = useRef({ keyword: "", activeOnly: false });
  const [popoverEl, setPopoverEl] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const menuOpen = Boolean(menuEl);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [initialSettingOpen, setInitialSettingOpen] = useState(false);
  const [itemLevelCds, setItemLevelCds] = useState([]);
  const [custLevelCds, setCustLevelCds] = useState([]);
  const [itemLevelCd, setItemLevelCd] = useState({});
  const [custLevelCd, setCustLevelCd] = useState({});
  const versions = useRef([]);
  const selectedVersion = useRef({});
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [isDelete, setIsDelete] = useState(false);
  const disaggTargetObj = useRef({});
  const [applySnapshot, setApplySnapshot] = useState(false);
  const [noteFlg, setNoteFlg] = useState(false);
  const [avgFlg, setAvgFlg] = useState(false);
  const [avgMonths, setAvgMonths] = useState(3);
  const [posAvgMonths, setPosAvgMonths] = useState(3);
  const [posAvgFlg, setPosAvgFlg] = useState(false);
  // BF Source Data 토글 — BF Settings로 옮겨서 이 화면은 읽기 전용으로만 쓴다(POS 관련 항목 노출 여부 판단용).
  const [posActualFlg, setPosActualFlg] = useState(false);

  /* 라벨은 값(cd)에서 그때그때 만든다 — 행 데이터에 번역문을 담아 두면 언어를 바꿔도
     state 가 그대로라 새로 조회할 때(=새로고침)까지 옛 언어가 남는다. */
  const avgMonthLabel = (cd) => `${cd}${t("months")}`;

  const periodUomOptions = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(cd => ({ cd, name: avgMonthLabel(cd) }));

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title: "measureMgmt", viewName });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * MeasureNameSettings 는 memo로 감싸 놨는데, 이 배열을 인라인으로 매번 새로 만들면(filter/map) 참조가
   * 매 렌더마다 달라져서 memo가 무력화된다 — 그리드 셀 편집처럼 이 목록과 무관한 상태가 바뀔 때도
   * MeasureNameSettings가 통째로 다시 그려지던 원인. 입력값이 안 바뀌면 같은 배열을 그대로 돌려준다.
   */
  const dpDisplayNameMeasures = useMemo(
    () => (posActualFlg
      ? dpDisplayNameMeasureList
      : dpDisplayNameMeasuresRaw.filter(measure => !CHANNEL_ONLY_MEASURE_CDS.includes(measure.measureCd)).map(measure => measure.measure)),
    [posActualFlg, dpDisplayNameMeasureList, dpDisplayNameMeasuresRaw]
  );

  const menuClose = () => {
    setMenuEl(null);
  };

  const popoverClose = () => {
    setPopoverEl(null);
  };

  const popoverOpen = Boolean(popoverEl);
  const popoverId = popoverOpen ? "simple-popover" : null;

  /*
   * 편집 가능 여부를 알리던 셀 배경(lightBlue[50]) / 수식 비대상 회색 블록(grey[200])은 걷어냈다.
   * 컬럼이 9개라 색 블록이 겹치면 표가 시끄러워져, 빈 값은 "—"(MeasureTextCell)로만 알린다.
   */

  /*
   * 오른쪽 편집 패널 — 목록에서 고른 행 하나를 편집한다.
   * 값 쓰기는 반드시 node.setDataValue 를 거친다. 그래야 기존 onCellValueChanged(짝 measure 동기화,
   * LR 버전 처리)와 DataState 변경행 추적이 그대로 돌아 저장/검증 경로를 손대지 않아도 된다.
   */
  // 노드는 참조로 들고 있는다 — 방금 추가한 행(applyTransaction)은 id 로 되찾지 못하는 경우가 있다.
  const selectedNode = selectedNodeRef.current;
  const selectedRow = selectedNode ? selectedRowSnapshot : null;

  // 목록을 다시 불러오면(저장/삭제 후) 들고 있던 노드는 버린다.
  useEffect(() => {
    selectedNodeRef.current = null;
    setSelectedNodeId(null);
    setSelectedRowSnapshot(null);
  }, [measureData]);

  /*
   * 개수는 그리드에서 센다 — 행 추가(applyTransaction)는 measureData state 를 거치지 않아
   * measureData.length 로 세면 방금 추가한 줄이 빠져 "30 / 29" 같은 숫자가 나온다.
   */
  const refreshMeasureCounts = api => {
    if (!api || api.isDestroyed?.()) return;

    let total = 0;
    let active = 0;
    api.forEachNode(node => {
      total += 1;
      if (node?.data?.activeFlg) {
        active += 1;
      }
    });

    setMeasureCounts({ shown: api.getDisplayedRowCount(), total, active });
  };

  const pickMeasureNode = node => {
    selectedNodeRef.current = node ?? null;
    setSelectedNodeId(node?.id ?? null);
    setSelectedRowSnapshot(node?.data ? { ...node.data } : null);
  };

  const setSelectedValue = (key, value) => {
    if (!selectedNode) return;

    let next = value;
    /*
     * 이름 칸을 비우면 코드가 정한 기본 이름(= 번역 키)으로 되돌린다.
     * 빈 이름은 저장할 수 없고(@NotBlank), 사용자 입장에서 "지운다 = 원래 이름으로"가 자연스럽다.
     */
    if (key === "name" && typeof next === "string" && next.trim() === "") {
      const defaultNameKey = dpMeasure[selectedNode.data?.measureCd]?.name;
      if (defaultNameKey) {
        next = defaultNameKey;
      }
    }

    // 짝 measure 동기화·변경행 추적이 걸린 기존 경로(onCellValueChanged)를 그대로 태운다.
    selectedNode.setDataValue(key, next);
    /*
     * 저장할 때 수식의 이름을 코드로 되돌릴지는 formulaExpValue 가 있는지로 판단한다(기존 규칙).
     * 패널에서 수식을 고쳤을 때도 같이 채워야 저장된 수식이 이름 그대로 남지 않는다.
     */
    if (key === "formulaExp") {
      selectedNode.setDataValue("formulaExpValue", value);
    }
    setSelectedRowSnapshot({ ...selectedNode.data });
  };

  /*
   * 선택 목록. 지금 값이 목록에 없는 경우가 있다 —
   * 예: aggregation 은 고를 수 있는 값이 SUM 하나뿐인데 기존 데이터는 NO AGGREGATION 이다.
   * 그때는 지금 값을 (고를 수 없는) 항목으로 앞에 끼워 넣어 빈칸으로 보이지 않게 한다.
   */
  const toOptions = (list, getKey, getLabel, current) => {
    const currentKey = isNil(current) ? "" : String(getKey(current));
    const options = (list ?? []).map(item => {
      const key = String(getKey(item));
      return { key, value: item, label: getLabel(item), selected: key === currentKey };
    });

    if (!isNil(current) && !options.some(option => option.selected)) {
      options.unshift({ key: currentKey, value: current, label: getLabel(current), selected: true, disabled: true });
    }

    return options;
  };

  const buildPanelFields = () => {
    if (!selectedRow) return [];

    const params = { data: selectedRow };
    const isBasic = includes(dpBasicMeasureCds, selectedRow.measureCd);
    const ruleEditable = !includes(nonRuleMeasureCds, selectedRow.measureCd) && !selectedRow.computeFlg;
    // arrivalQty(BOH) 등은 다른 규칙은 잠겨 있어도 aggregation 규칙만은 DP setting에서 설정 가능
    const aggRuleEditable = (ruleEditable || includes(aggEditableMeasureCds, selectedRow.measureCd)) && !selectedRow.computeFlg;
    const isMf = ["MF_AMT", "MF_QTY", "SF_AMT", "SF_QTY"].includes(selectedRow.measureCd);
    const optionList = optionCellEditorParams(params);

    /*
     * 이름 칸은 "보여 주는 칸"이 아니라 "고치는 칸"이다 — 번역된 글자를 미리 채워 두면 안 된다.
     * 기본 measure 는 name 에 번역 키(financeAmt)가 들어 있고 화면에서만 언어별로 풀린다.
     * 번역된 글자를 칸에 채워 두면 한 글자만 고쳐도 그 키가 "지금 언어의 글자"로 굳어,
     * 다른 언어로 접속한 사용자에게도 그 글자가 그대로 보인다.
     * 그래서 아직 안 바꾼 measure 는 칸을 비워 두고 기본 이름은 흐린 안내글로만 깔고,
     * 사용자가 실제로 친 글자가 있을 때만 그 글자를 값으로 들고 있는다.
     * (칸을 비우면 setSelectedValue 가 다시 번역 키로 되돌린다.)
     */
    const defaultNameKey = dpMeasure[selectedRow.measureCd]?.name;
    const defaultName = defaultNameKey ? GridUtils.gridValueL10N(defaultNameKey) : null;
    // 번역 키 그대로면 "안 바꾼 것" — 라벨이 아니라 저장된 원본끼리 견준다.
    const renamed = !!defaultNameKey && selectedRow.name !== defaultNameKey;
    const customName = defaultNameKey && !renamed ? "" : (selectedRow.name ?? "");

    return [
      {
        key: "name", label: t("measure"), kind: "text",
        value: customName,
        placeholder: defaultName ?? undefined,
        onReset: renamed ? () => setSelectedValue("name", defaultNameKey) : undefined,
        resetText: defaultName,
        resetLabel: t("MSG_ResetMeasureName"),
      },
      {
        key: "valueTypeCd", label: t("type"), kind: "select", disabled: isBasic,
        options: toOptions(measureType.length < 1 ? valueType.current : measureType,
          v => v, v => GridUtils.gridValueL10N(v), selectedRow.valueTypeCd),
      },
      {
        key: "aggRuleCd", label: t("aggregation"), kind: "select", disabled: !aggRuleEditable,
        options: toOptions(aggRules, r => r.cd, r => GridUtils.gridValueL10N(r.name), selectedRow.aggRuleCd),
      },
      {
        key: "disaggRuleCd", label: t("disaggregation"), kind: "select", disabled: !ruleEditable,
        options: toOptions(isMf ? defaultRule : disaggRules, r => r.cd,
          r => GridUtils.gridValueL10N(r.name), selectedRow.disaggRuleCd),
      },
      {
        key: "option", label: t("option"), kind: "select", disabled: !optionEditable(params),
        options: toOptions(optionList, o => o.id ?? o.cd, o => optionFormatter({ data: selectedRow, value: o }),
          selectedRow.option),
      },
      {
        key: "descTxt", label: t("descTxt"), kind: "text",
        value: selectedRow.descTxt,
      },
      {
        key: "formulaExp", label: t("formula"), kind: "formula", wide: true,
        value: selectedRow.formulaExp,
        disabled: !selectedRow.computeFlg || isBasic,
        // 작성법 안내(팝오버)는 수식 칸 옆에서 연다.
        onHelp: e => setPopoverEl(e.currentTarget),
        // compute 인데 왜 안 눌리는지 화면에서 바로 알 수 있게 이유를 적어준다.
        help: selectedRow.computeFlg && isBasic ? t("MSG_BasicMeasureFormulaLocked") : undefined,
        // 수식 칸에서 "[" 를 치면 measure 고르는 메뉴가 뜨던 동작을 그대로 옮긴다.
        insert: formulaInsert,
        onKeyDown: e => {
          if (e.key === "[" && selectedRow.computeFlg) {
            setMenuEl(e.currentTarget);
          }
        },
      },
    ];
  };

  // 저장하지 않은 편집을 버리고 서버 값으로 되돌린다(IP/RP Settings 의 Reset 과 같은 자리).
  const handleResetMeasures = () => {
    pickMeasureNode(null);
    retrieveMeasures();
  };

  // 검색어(measure 이름/코드) + Active only 를 AG Grid external filter 로 건다.
  const isMeasureFilterPresent = () => {
    const { keyword, activeOnly: onlyActive } = measureFilterRef.current;
    return onlyActive || keyword.trim() !== "";
  };

  const doesMeasureFilterPass = node => {
    const { keyword, activeOnly: onlyActive } = measureFilterRef.current;
    const data = node?.data ?? {};

    if (onlyActive && !data.activeFlg) {
      return false;
    }

    const word = keyword.trim().toLowerCase();
    if (!word) {
      return true;
    }

    // name 은 번역 키(financePlanAmt)라 화면에 보이는 라벨과 다르다 — 둘 다 대상으로 찾는다.
    return String(GridUtils.measureLabel(data.name, dpMeasure[data?.measureCd]?.name) ?? "").toLowerCase().includes(word)
      || String(data.name ?? "").toLowerCase().includes(word)
      || String(data.measureCd ?? "").toLowerCase().includes(word);
  };

  const applyMeasureFilter = next => {
    measureFilterRef.current = { ...measureFilterRef.current, ...next };
    gridApi?.onFilterChanged();
  };

  const statusValueCellRenderer = ({ value }) => {
    return GridUtils.gridValueL10N(value);
  };

  const checkboxClickEvent = (e, params) => {
    if (!includes(dpBasicMeasureCds, params?.data?.measureCd)) {
      params.node.setDataValue(params.column, !params.value);
    } else {
      e.preventDefault();
    }
  };

  const optionEditable = params => {
    const measureCd = params?.data?.measureCd;
    return includes([...financePlanMeasures, ...releasedMeasures, ...salesPlanMeasures, ...avgMeasures, ...posAvgMeasures], measureCd);
  };

  const optionFormatter = params => {
    const measureCd = params?.data?.measureCd;

    if (includes([...financePlanMeasures, ...salesPlanMeasures], measureCd)) {
      if (params.value) {
        return GridUtils.gridValueL10N(params.value.name);
      }
    } else if (includes([...avgMeasures, ...posAvgMeasures], measureCd)) {
      /* 행에 담긴 name 은 저장 시점 언어라 그대로 쓰면 언어를 바꿔도 옛 글자가 남는다 — cd 로 다시 만든다. */
      return isNil(params.value?.cd) ? "" : avgMonthLabel(params.value.cd);
    } else if (includes(releasedMeasures, measureCd)) {
      /* 계획 버전 이름은 사용자가 지은 값이라 번역 대상이 아니다. */
      return params.value?.name;
    }
    return "";
  };

  const optionCellEditorParams = params => {
    const measureCd = params?.data?.measureCd;

    if (includes([...financePlanMeasures, ...salesPlanMeasures], measureCd)) {
      return disaggTarget;
    } else if (includes(releasedMeasures, measureCd)) {
      return versions.current;
    } else if (includes([...avgMeasures, ...posAvgMeasures], measureCd)) {
      return periodUomOptions;
    }
    return [];
  };

  const CustomBooleanIcon = activeProps => {
    const { displayName, selectAll: select, setSelectAll: setSelect } = activeProps;

    const allCheckActive = e => {
      const allActive = e?.target?.checked;
      setSelect(allActive);
    };

    return (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Box>
          <input
            type="checkbox"
            defaultChecked={select}
            onClick={e => allCheckActive(e)}
          />
        </Box>
        <Box className="customHeaderLabel">{displayName}</Box>
      </Box>
    );
  };

  // 목록 한 줄에 들어갈 요약 — 이름·계산식여부·타입·사용자여부·원래이름·(집계 · 배분 · 옵션)
  const measureSummary = data => {
    const agg = GridUtils.gridValueL10N(data?.aggRuleCd?.name);
    const disagg = GridUtils.gridValueL10N(data?.disaggRuleCd?.name);
    const option = optionFormatter({ data, value: data?.option });

    /*
     * 이름을 바꾼 measure 는 원래(코드가 정한 기본) 이름을 같이 보여준다 —
     * 바꾼 이름만 보이면 이게 어떤 measure 였는지 알 수 없다.
     */
    const defaultKey = dpMeasure[data?.measureCd]?.name;
    // 기본값 그대로일 때만 번역한다 — 사용자가 지은 이름은 건드리지 않는다(GridUtils.measureLabel 주석).
    const name = GridUtils.measureLabel(data?.name, defaultKey);
    const defaultName = defaultKey ? GridUtils.gridValueL10N(defaultKey) : null;

    return {
      name,
      fx: !!data?.computeFlg,
      type: GridUtils.gridValueL10N(data?.valueTypeCd),
      user: !!data?.isUserMeasure,
      original: defaultName && defaultName !== name ? defaultName : null,
      meta: [agg, disagg, option].filter(Boolean).join(" · "),
    };
  };

  const measureColumnDefs = [
    {
      headerName: "",
      field: "id",
      type: ["nonEditableColumn", "defaultValueFormatter"],
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      headerName: "displayName",
      field: "measureCd",
      width: 85,
      hide: true,
      suppressColumnsToolPanel: true,
      pinned: "left", lockPinned: true,
    },
    {
      headerName: "activeFlg",
      field: "activeFlg",
      width: 76,
      editable: true,
      type: ["booleanColumn"],
      cellRenderer: MeasureActiveCell,
      pinned: "left", lockPinned: true,
      headerComponent: CustomBooleanIcon,
      headerComponentParams: {
        enableMenu: false,
        setSelectAll,
        selectAll,
      },
    },
    {
      headerName: "measure",
      field: "name",
      width: 200,
      editable: true,
      hide: true,
      valueFormatter: statusValueCellRenderer,
    },
    /*
     * 목록에 보이는 유일한 값 컬럼 — 이름·ƒ·타입·설정요약을 한 줄로 그린다.
     * 실제 편집은 오른쪽 패널이 하고, 아래 속성 컬럼들은 값 저장(setDataValue)과
     * 기존 저장/검증 경로를 위해 숨긴 채로 남겨둔다.
     */
    {
      colId: "measureSummary",
      headerName: "measure",
      flex: 1,
      minWidth: 260,
      editable: false,
      sortable: false,
      cellRenderer: MeasureListCell,
      cellRendererParams: { summary: data => measureSummary(data), userLabel: t("user") },
    },
    {
      headerName: "compute",
      field: "computeFlg",
      width: 100,
      hide: true,
      editable: params => !includes(dpBasicMeasureCds, params?.data?.measureCd),
      cellRenderer: MeasureComputeCell,
      cellRendererParams: { onToggle: (e, params) => checkboxClickEvent(e, params) },
      onCellClicked: params => {
        if (params.data?.simFlg) {
          params.node.setDataValue(params.column, !params.value);
        }
      },
    },
    {
      headerName: "type",
      field: "valueTypeCd",
      width: 68,
      hide: true,
      editable: params => !includes(dpBasicMeasureCds, params?.data?.measureCd),
      cellEditor: "agRichSelectCellEditor",
      cellRenderer: MeasureTypeCell,
      valueFormatter: statusValueCellRenderer,
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: () => {
        return {
          values: measureType.length < 1 ? valueType.current : measureType,
        };
      },
    },
    {
      headerName: "aggregation",
      field: "aggRuleCd",
      width: 150, minWidth: 120,
      hide: true,
      editable: params => (!includes(nonRuleMeasureCds, params?.data?.measureCd) || includes(aggEditableMeasureCds, params?.data?.measureCd)) && !params?.data?.computeFlg,
      valueFormatter: rule => {
        return GridUtils.gridValueL10N(rule?.value?.name);
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: () => {
        return {
          values: aggRules,
        };
      },
      cellRenderer: MeasureTextCell,
    },
    {
      headerName: "disaggregation",
      field: "disaggRuleCd",
      width: 165, minWidth: 130,
      hide: true,
      editable: params => !includes(nonRuleMeasureCds, params?.data?.measureCd) && !params?.data?.computeFlg,
      valueFormatter: rule => {
        return GridUtils.gridValueL10N(rule?.value?.name);
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      cellEditorParams: (params) => {
        let isMf = false;
        if (params?.data?.measureCd === "MF_AMT" || params?.data?.measureCd === "MF_QTY" || params?.data?.measureCd === "SF_AMT" || params?.data?.measureCd === "SF_QTY") {
          isMf = true;
        }
        return {
          values: isMf ? defaultRule : disaggRules,
        };
      },
      cellRenderer: MeasureTextCell,
    },
    {
      headerName: "option",
      field: "option",
      width: 110,
      hide: true,
      tooltipValueGetter: params => optionHelpText(params.data?.optionHelpCd),
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      editable: params => {
        return optionEditable(params);
      },
      valueFormatter: params => {
        return optionFormatter(params);
      },
      cellEditorParams: params => {
        return {
          values: optionCellEditorParams(params),
        };
      },
      cellRenderer: MeasureTextCell,
    },
    {
      headerName: "formula",
      field: "formulaExp",
      width: 190,
      hide: true,
      editable: params => params.data.computeFlg && !includes(dpBasicMeasureCds, params?.data?.measureCd),
      cellRenderer: MeasureTextCell,
      cellRendererParams: { mono: true },
      menuTabs: ["generalMenuTab"],
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        isQuestion: true,
        enableMenu: false,
        onClickedEvent: setPopoverEl,
      },
    },
    {
      headerName: "formula",
      field: "formulaExpValue",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      headerName: "descTxt",
      field: "descTxt",
      editable: true,
      width: 200,
      hide: true,
      cellRenderer: MeasureTextCell,
      cellRendererParams: { placeholder: t("addNote") },
    },
    {
      headerName: "verNum",
      field: "verNum",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "simFlg",
      hide: true,
    },
  ];

  const onGridReady = params => {
    DataState.initialize(params.api);
    setGridApi(params.api);
    /*
     * 목록형이라 저장된 컬럼 상태(p13n)를 복원하지 않는다 —
     * 예전 표 레이아웃의 상태가 남아 있으면 숨겨둔 속성 컬럼이 다시 튀어나온다.
     */
    params.api.setGridOption("columnDefs", GridUtils.getColumnDefs(measureColumnDefs));
    // 언어 변경 시 헤더를 다시 번역할 수 있도록 번역 전 원본을 등록한다.
    GridUtils.registerLocalizedGrid(params.api, measureColumnDefs);
  };

  /*
   * 수식 칸에서 "[" 를 쳐서 고른 measure 를 커서 자리에 끼워 넣는다.
   * 예전에는 그리드 셀을 직접 고쳤지만, 지금은 오른쪽 패널 입력칸이 편집 중인 값을 들고 있으므로
   * 넣을 조각만 내려보내고 실제 삽입은 입력칸이 한다(커서 위치 유지).
   */
  const addExpression = measureName => {
    formulaInsertSeq.current += 1;
    setFormulaInsert({ text: `${GridUtils.gridValueL10N(measureName)}]`, seq: formulaInsertSeq.current });
    menuClose();
  };

  const getRuleName = (cd, isAgg) => {
    if (!isAgg && cd === "RATIO") {
      return codeUtil.dpDisAggRules.ITEM_RATIO;
    }
    return isAgg ? codeUtil.dpAggRules[cd] : codeUtil.dpDisAggRules[cd];
  };

  const onCellKeyDown = e => {
    if (isEqual(e.column.colId, "formulaExp") && isEqual(e.event.key, "[") && e.data.computeFlg) {
      const path = [];
      const getParent = (list, element) => {
        if (element?.parentElement) {
          list.push(element?.parentElement);
          return getParent(list, element?.parentElement);
        }
        return null;
      };
      getParent(path, e.event.target);
      setMenuEl(path[3]);
      e.node.setSelected(true);
    }
  };

  const sortListByColumn = (type, list, columnName) => {
    if (type === "desc") {
      list.sort((a, b) => {
        if (a[columnName] > b[columnName]) {
          return -1;
        }
        if (a[columnName] < b[columnName]) {
          return 1;
        }
        return 0;
      });
    } else {
      list.sort((a, b) => {
        if (a[columnName] > b[columnName]) {
          return 1;
        }
        if (a[columnName] < b[columnName]) {
          return -1;
        }
        return 0;
      });
    }

    return list;
  };

  const setGridDatas = modalData => {
    setOriMeasureList(modalData);
    const datas = [];
    const types = [];
    for (const measure of modalData) {
      let selectVer = [];
      const isLr = measure.measureCd === dpMeasure.LR_AMT.cd || measure.measureCd === dpMeasure.LR_QTY.cd;
      const isFf = measure.measureCd === dpMeasure.FF_AMT.cd || measure.measureCd === dpMeasure.FF_QTY.cd;
      const isSp = measure.measureCd === dpMeasure.SALES_PLAN_AMT.cd || measure.measureCd === dpMeasure.SALES_PLAN_QTY.cd;
      const isAsa = measure.measureCd === dpMeasure.ASA_AMT.cd || measure.measureCd === dpMeasure.ASA_QTY.cd;
      const isPosAsa = measure.measureCd === dpMeasure.POS_ASA_QTY.cd;

      // BF Source Data(POS_ACTUAL_FLG)가 꺼져 있으면 채널 전용 측정치는 전부 목록에서 뺀다 — BF Settings로
      // 옮긴 토글이 꺼진 상태라 POS 계열 데이터를 안 쓰기로 한 것이므로, Workbench Measure 시트에 노출하지 않는다.
      if (!posActualFlg && CHANNEL_ONLY_MEASURE_CDS.includes(measure.measureCd)) {
        continue;
      }

      if (isLr) {
        if (selectedVersion.current && versions.current) {
          const versionId = selectedVersion.current?.objVal?.LR_VER_ID;
          selectVer = versions.current.filter(el => el.id === versionId);
        }
      }
      if (!measure.simFlg) {
        const row = {
          id: measure.id,
          computeFlg: measure.computeFlg,
          measureCd: measure.measureCd,
          name: measure.name,
          valueTypeCd: measure.valueTypeCd,
          formulaExpValue: measure.formulaExp ? measure.formulaExp : null,
          formulaExp: measure.formulaExp ? WorkbenchUtil.changeExpression(measure.formulaExp, modalData, true) : null,
          aggRuleCd: getRuleName(measure.aggRuleCd, true),
          disaggRuleCd: getRuleName(measure.disaggRuleCd, false),
          activeFlg: measure.activeFlg,
          simFlg: measure.simFlg,
          owner: measure.owner,
          descTxt: measure.descTxt,
          option: selectVer?.[0],
          /* 툴팁 문구는 행에 담지 않는다 — 번역문을 state 에 굳히면 언어를 바꿔도 그대로 남는다.
             어떤 안내를 띄울지만 키로 담고, 글자는 tooltipValueGetter 가 그릴 때 만든다. */
          optionHelpCd: isLr ? "RELEASE_VERSION" : "",
          verNum: measure.verNum,
          isUserMeasure: measure.measureCd.includes(USER_MEASURE) ? true : false,
        };

        if (isFf) {
          row.option = !isEmpty(disaggTargetObj.current) ? dpDisAggTarget[disaggTargetObj.current.strVal] : dpDisAggTarget.QTY;
          row.optionHelpCd = "DISAGG_TARGET";
        }
        if (isSp) {
          row.option = disaggTargetObj?.current?.objVal?.target ? dpDisAggTarget[disaggTargetObj?.current?.objVal?.target] : dpDisAggTarget.QTY;
          row.optionHelpCd = "DISAGG_TARGET";
        }
        if (isAsa) {
          row.option = periodUomOptions.find(option => option.cd === avgMonths);
        }
        if (isPosAsa) {
          row.option = periodUomOptions.find(option => option.cd === posAvgMonths);
        }

        row.originData = cloneDeep(row);
        types.push(row.valueTypeCd);
        types.push(...valueType.current);
        datas.push(row);
      }
    }

    let result = sortListByColumn("asc", datas, "name");
    result = sortListByColumn("asc", result, "isUserMeasure");
    result = sortListByColumn("desc", result, "activeFlg");

    setMeasureType(uniq(types));
    setMeasureData(result);
    // v26.10: POS 미출시 — SHOW_POS 가 꺼져 있으면 POS_QTY 를 target 으로 내놓지 않는다
    setInitialTarget(result.filter(item => item.measureCd.includes("SF") || item.measureCd.includes("MF")
      || (codeUtil.SHOW_POS && item.measureCd.includes("POS_QTY"))));
  };

  const settingInitialConfig = result => {
    if (!isEmpty(result)) {
      if (result.strVal) {
        setDefaultValue(result.strVal);
      }
      if (isEqual(result.strVal, CUSTOMIZATION)) {
        setCustomInitialData(result);
      }
      if (result.objVal && Object.keys(result.objVal).length > 0) {
        for (const key of Object.keys(result.objVal)) {
          if (key === CUST_LEVEL_ID) {
            const cust = custLevelCds.filter(function (el) {
                return el.id === result.objVal[key];
              })[0] ?? CUSTOMER;
            setCustLevelCd(cust);
          } else if (key === ITEM_LEVEL_ID) {
            const item =
              itemLevelCds.filter(function (el) {
                return el.id === result.objVal[key];
              })[0] ?? ITEM;
            setItemLevelCd(item);
          } else if (key === ROLLING_RANGE) {
            const val = result.objVal[key] ?? 0;
            setRollingRange(val);
          } else {
            if (key !== "defaultValue" && key !== CUSTOMIZATION && key !== "copyTarget") {
              setComputeMeasureList(result.objVal[key]);
            }
          }
        }
      } else {
        const defaultComputeList = WorkbenchUtil.getDefaultComputeList(result.strVal);
        setComputeMeasureList(defaultComputeList);
      }
    }
  };

  const setConfigDatas = resultData => {
    const idObj = {};
    for (const result of resultData) {
      const configCd = result.configCd;
      if (isEqual(configCd, BUCKET_UNIT)) {
        setBucketUnit(result.strVal);
        if (result.strVal === PARTIAL_WEEK) {
          for (const day of dayOfWeek) {
            if (day.cd !== dayOfWeeks.MONDAY.cd) {
              day.disabled = true;
            }
          }
        } else {
          for (const day of dayOfWeek) {
            day.disabled = false;
          }
        }
      } else if (isEqual(configCd, START_DAY)) {
        setStartDay(result.strVal ?? values(dayOfWeek)[0].cd);
      } else if (isEqual(configCd, PLANNING_RANGE)) {
        setPlanRange(result.numVal);
      } else if (isEqual(configCd, DTF)) {
        setDTF(result.numVal);
      } else if (isEqual(configCd, RUN_BF)) {
        setRunBf(result.boolVal);
      } else if (isEqual(configCd, INITIAL_DEMAND)) {
        settingInitialConfig(result);
      } else if (isEqual(configCd, PLAN_ITEM)) {
        setPlanItem(result.boolVal);
      } else if (isEqual(configCd, DISAGGREGATION_TARGET)) {
        disaggTargetObj.current = result;
      } else if (isEqual(configCd, LR)) {
        selectedVersion.current = result;
      } else if (isEqual(configCd, DEMAND_NETTING)) {
        setIsDpNetting(result.boolVal);
        setDpNettingUnit(result.strVal ?? QTY);
      } else if (isEqual(configCd, APPLY_SNAPSHOT)) {
        setApplySnapshot(result.boolVal);
      } else if (isEqual(configCd, NOTE_FLG)) {
        setNoteFlg(result.boolVal);
      } else if (isEqual(configCd, AVG_FLG)) {
        setAvgFlg(result.boolVal);
        setAvgMonths(result.numVal);
        setPosAvgFlg(result.objVal?.POS_AVG_FLG ?? false);
      } else if (isEqual(configCd, POS_ACTUAL_FLG)) {
        setPosAvgMonths(result.numVal ?? 3);
        setPosActualFlg(result.boolVal ?? false);
      }
      idObj[configCd] = result;
    }
    setIdMap(idObj);
  };

  const initialPlanConfig = () => {
    const param = {
      moduleCd: DP,
      activeFlg: true,
    };

    planConfigService
      .getAll(param)
      .then(response => {
        if (response.data?.results) {
          const resultData = response.data.results;
          setConfigDatas(resultData);
        } else {
          setBucketUnit(bucketUnit);
        }
      })
      .catch(e => {
        debug(e);
      })
      .finally(() => {
        setIsSetConfig(true);
      });
  };

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: module.DP,
      activeFlg: true,
      configCd: LR,
    };

    planConfigService
      .getConfigCd(param)
      .then(response => {
        if (response?.data) {
          selectedVersion.current = response.data;
        } else {
          const row = {
            moduleCd: module.DP,
            activeFlg: true,
            configCd: LR,
            boolVal: false,
            objVal: {},
          };
          selectedVersion.current = row;
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveMeasures = () => {
    measureService
      .getAll({ isBasic: true })
      .then(response => {
        if (response.data?.results) {
          setGridDatas(response.data.results);
        }
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        DataState.initialize(gridApi);
      });
  };

  const retrieveHrchyConfig = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a, b) => {
            if (a.seqNum < b.seqNum) {
              return -1;
            }

            if (a.seqNum > b.seqNum) {
              return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            } else {
              custConfig.push(row);
            }
          }

          setItemLevelCds([...itemConfig, ITEM]);
          setCustLevelCds([...custConfig, CUSTOMER]);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrievePlanVersion = () => {
    const statusCds = [RELEASED];
    planVersionService
      .getAll({ moduleCd: DP, statusCds, pagination: false, orderByColumn: "releaseTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          const releasedVersion = [];
          for (const result of results) {
            if (isEqual(RELEASED, result.statusCd)) {
              releasedVersion.push(result);
            }
          }
          versions.current = releasedVersion;
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const setActiveCheck = allStateData => {

    let isChange = false;
    const customMeasures = allStateData.filter(m => m.measureCd?.includes(USER_MEASURE));
    const customFormulas = customMeasures.map(m => m.formulaExpValue);
    const customMeasureCds = customMeasures.map(m => m?.measureCd);

    const userMeasures = measureData.filter(m => m?.measureCd.includes(USER_MEASURE));

    for (const userMeasure of userMeasures) {
      for (const customFormula of customFormulas) {

        if (includes(customFormula, userMeasure.measureCd) && !includes(customMeasureCds, userMeasure.measureCd)) {
          const cloneSimMeasure = cloneDeep(userMeasure);
          cloneSimMeasure.activeFlg = true;
          allStateData.push(cloneSimMeasure);
          isChange = true;
        }
      }
    }
    return isChange;
  };

  const handleSave = () => {
    if (defaultValue === BASELINE_FORECAST) {
      /*
       * 레벨을 직접 고르지 않으면 state 는 비어 있지만, 화면 Select 는 첫 레벨(All)을 기본으로 보여 주고
       * 저장 값도 같은 첫 레벨로 채운다(savePlanConfig 의 `custLevelCd?.id ?? custLevelCds[0].id`).
       * 그래서 "화면에 선택돼 보이는데 저장하면 레벨을 고르라"는 어긋남이 없게, 선택지가 있으면 그 기본값을 선택한 것으로 본다.
       * 선택지 자체가 아직 없을 때(계층 설정 조회 전)만 막는다.
       */
      const effectiveCustLevel = !isEmpty(custLevelCd) ? custLevelCd : custLevelCds[0];
      const effectiveItemLevel = !isEmpty(itemLevelCd) ? itemLevelCd : itemLevelCds[0];
      if (isEmpty(effectiveCustLevel) || isEmpty(effectiveItemLevel)) {
        setDialogInfo({open: true, title: "warning", content: t("MSG_ConfirmLevel"), el: {type: "CONFIG"}});
        return;
      }
      if (isEmpty(custLevelCd)) {
        setCustLevelCd(effectiveCustLevel);
      }
      if (isEmpty(itemLevelCd)) {
        setItemLevelCd(effectiveItemLevel);
      }
    }
    if ((defaultValue !== CUSTOMIZATION && defaultValue !== NOTINITIAL)  && size(computeMeasureList) < 1) {
      setSnackInfo({ open: true, severity: "error", content: t("MSG_ComputeSettingRequired") });
      return;
    }
    if (idMap?.[BUCKET_UNIT]?.strVal) {
      if (!isEqual(idMap?.[BUCKET_UNIT]?.strVal, bucketUnit)) {
        setDialogInfo({open: true, title: "save", content: t("MSG_BucketUnitConfirm"), el: {type: "CONFIG"}});
        setIsDelete(true);
      } else {
        setDialogInfo({open: true, title: "save", content: t("MSG_SaveConfirm"), el: {type: "CONFIG"}});
        setIsDelete(false);
      }
    } else {
      setDialogInfo({open: true, title: "save", content: t("MSG_SaveConfirm"), el: {type: "CONFIG"}});
      setIsDelete(false);
    }
  };

  const saveMeasure = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const isChange = setActiveCheck(allStateData);

    if (allStateData.length > 0) {
      const updateDatas = allStateData.map(row => {
        if (row.formulaExpValue) {
          row.formulaExp = WorkbenchUtil.changeExpression(row.formulaExp, oriMeasureList, false);
        }
        row.aggRuleCd = row.aggRuleCd.cd;
        if (row.disaggRuleCd?.cd) {
          row.disaggRuleCd = row.disaggRuleCd.cd;
        } else {
          row.disaggRuleCd = row.disaggRuleCd ?? disaggRules[0].cd;
        }
        return row;
      });

      measureService
        .basicUpsert(updateDatas)
        .then(() => {
          if (isChange) {
            setSnackInfo({ open: true, severity: validation, content: t("MSG_FormulaUserMeasureValidation") });
          } else {
            setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
          }
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
          debug(e);
        })
        .then(() => {
          initialPlanConfig();
          retrievePlanConfig();
        });
    }
  };

  const savePlanConfig = () => {
    let saveItem = {};
    const hasPlanRange = dtf <= planRange;
    if(!hasPlanRange) {
      return "MSG_DpSetPlanRange";
    }
    const saveList = [];
    for (const mandatory of mandatorySave) {
      if (Object.keys(idMap).length > 0 && idMap[mandatory]) {
        saveItem = {...idMap[mandatory]};
      } else {
        saveItem = {
          moduleCd: DP,
          configCd: `${mandatory}`,
          activeFlg: true,
          boolVal: false,
        };
      }
      if (isEqual(mandatory, BUCKET_UNIT)) {
        saveItem.strVal = bucketUnit;
      } else if (isEqual(mandatory, PLANNING_RANGE)) {
        saveItem.numVal = planRange;
      } else if (isEqual(mandatory, DTF)) {
        saveItem.numVal = dtf;
      } else if (isEqual(mandatory, START_DAY)) {
        if (codeUtil.isWeekBucket(bucketUnit)) {
          saveItem.strVal = startDay;
        }
      } else if (isEqual(mandatory, RUN_BF)) {
        saveItem.boolVal = runBf;
      } else if (isEqual(mandatory, INITIAL_DEMAND)) {
        let data = {};
        data[defaultValue] = computeMeasureList?.length > 0 ? computeMeasureList : [];
        if (defaultValue === BASELINE_FORECAST || (defaultValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList))) {
          data[CUST_LEVEL_ID] = custLevelCd?.id ?? custLevelCds[0].id;
          data[ITEM_LEVEL_ID] = itemLevelCd?.id ?? itemLevelCds[0].id;
        }

        if (isEqual(defaultValue, CUSTOMIZATION)) {
          data = customInitialData?.objVal;
        }

        data = {...data, [ROLLING_RANGE]: rollingRange};
        saveItem.objVal = data;
        saveItem.strVal = defaultValue;
      } else if (isEqual(mandatory, PLAN_ITEM)) {
        saveItem.boolVal = planItem;
      } else if (isEqual(mandatory, DEMAND_NETTING)) {
        saveItem.boolVal = isDpNetting;
        saveItem.strVal = dpNettingUnit;
      } else if (isEqual(mandatory, APPLY_SNAPSHOT)) {
        saveItem.boolVal = applySnapshot;
      } else if (isEqual(mandatory, NOTE_FLG)) {
        saveItem.boolVal = noteFlg;
      } else if (isEqual(mandatory, AVG_FLG)) {
        //save together
        saveItem.numVal = avgMonths;
        saveItem.boolVal = avgFlg;
        saveItem.objVal = { ...(saveItem.objVal ?? {}), POS_AVG_FLG: posAvgFlg };
      } else if (isEqual(mandatory, POS_ACTUAL_FLG)) {
        saveItem.numVal = posAvgMonths;
      }
      saveItem.isDeleteFlg = isDelete;

      saveList.push(saveItem);
    }

    const paramList = [];
    if (selectedVersion?.current?.objVal?.LR_VER_ID) {
      paramList.push({ ...selectedVersion.current });
    }
    if (isEmpty(disaggTargetObj.current)) {
      const row = {
        moduleCd: module.DP,
        activeFlg: true,
        configCd: DISAGGREGATION_TARGET,
        boolVal: false,
        strVal: disaggTarget[0].cd,
      };
      paramList.push({ ...row });
    } else {
      paramList.push({ ...disaggTargetObj.current });
    }

    planConfigService
      .upsert([...saveList, ...paramList])
      .then(response => {
        if (response.data) {
          setConfigDatas(response.data);
          const allStateData = DataState.getAllStateData(gridRef.current.api);
          if (allStateData.length <= 0) {
            setSnackInfo({ open: true, severity: saveSuccess, content: t("MSG_SaveSuccess") });
          }
        }
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .finally(() => {
        saveMeasure();
      });

  };

  /*
   * 삭제는 오른쪽 패널에서 고른 measure 하나를 지운다(체크박스 열 없이).
   * 아직 저장 전인 새 행은 서버에 없으므로 그리드에서만 걷어낸다.
   */
  const removeMeasure = () => {
    gridApi.stopEditing(false);

    const target = selectedRow;
    if (!target) {
      setSnackInfo({open: true, severity: noSaveData, content: t("MSG_NoSaveData")});
      return;
    }

    if (isNil(target.id)) {
      gridApi.applyTransaction({ remove: [selectedNode.data] });
      pickMeasureNode(null);
      refreshMeasureCounts(gridApi);
      return;
    }

    const itemIds = [target.id];

    if (itemIds.length > 0) {
      measureService
        .remove(itemIds)
        .then(() => {
          setSnackInfo({open: true, severity: saveSuccess, content: t("MSG_DeleteSuccess")});
        })
        .catch(e => {
          const message = e.response?.data;
          setSnackInfo({open: true, severity: saveFail, content: t(message ? message : "MSG_DeleteFailed")});
          debug(e);
        })
        .then(() => {
          retrieveMeasures();
        });
    } else {
      setSnackInfo({open: true, severity: noSaveData, content: t("MSG_NoSaveData")});
    }
  };

  const handleDialog = (action, _falseAction) => {
    if (action === "SAVE") {
      savePlanConfig();
      saveMeasureDisplayNames();
    } else if (action === "DELETE") {
      removeMeasure();
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const handelIntialSettingClose = () => {
    setInitialSettingOpen(false);
  };

  const handleChangeSelect = event => {
    const targetNm = event?.target?.name;
    const targetVal = event?.target?.value;

    if (targetNm === "startDay") {
      setStartDay(targetVal);
    } else if (targetNm === "defaultValue") {
      setDefaultValue(targetVal);
      const defaultComputeList = WorkbenchUtil.getDefaultComputeList(targetVal);
      setComputeMeasureList(defaultComputeList);
    } else if (targetNm === "bucketUnit") {
      setBucketUnit(targetVal);
      //bucket이 partial week이면 월요일만 선택가능
      if (targetVal === PARTIAL_WEEK) {
        for (const day of dayOfWeek) {
          if (day.cd !== dayOfWeeks.MONDAY.cd) {
            day.disabled = true;
          }
        }
        setStartDay(dayOfWeeks.MONDAY.cd);
      } else {
        for (const day of dayOfWeek) {
          day.disabled = false;
        }
      }
    } else if (targetNm === "custLevel") {
      const cust = custLevelCds.filter(function (el) {
          return el.id === targetVal;
        })[0] ?? CUSTOMER;
      setCustLevelCd(cust);
    } else if (targetNm === "itemLevel") {
      const item = itemLevelCds.filter(function (el) { return el.id === targetVal })[0] ?? ITEM;
      setItemLevelCd(item)
    } else if (targetNm === "noteFlg") {
      setNoteFlg(!noteFlg)
    }
  };

  const setNodeData = (colId, params) => {
    const measureCd = params.data?.measureCd;
    const pair = dpMeasure[measureCd]?.pair;
    gridRef.current.api.forEachNode(node => {
      const rowNodeData = node?.data;
      if (node?.rowIndex === null) {
        return;
      }
      if (rowNodeData.measureCd !== measureCd && rowNodeData.measureCd !== pair) {
        return;
      }
      rowNodeData[colId] = params.newValue;
      const copy = cloneDeep(rowNodeData);
      delete copy.originData;
      const updateRow = gridApi?.stateRows?.updated;
      if (!rowNodeData?.originData) {
        return;
      }
      if (isEqual(rowNodeData?.originData, copy)) {
        delete node.rowState;
        DataState.setDataState(node);
        updateRow.splice(updateRow.indexOf(node.rowIndex), 1);
      } else {
        rowNodeData[colId] = params.value;
        node.rowState = UPDATED;
        DataState.setDataState(node);
      }
    });
  };

  const onCellValueChanged = params => {
    const { column, oldValue, newValue } = params;
    const colId = column.getColId();
    if (!isEqual(oldValue, newValue)) {
      if (isEqual(colId, "option")) {
        if (params.data.measureCd === dpMeasure.LR_AMT.cd || params.data.measureCd === dpMeasure.LR_QTY.cd) {
          const version = params.value;
          const obj = {
            LR_VER_ID: version.id,
          };
          selectedVersion.current.objVal = obj;
          setNodeData(colId, params);
        } else if (params.data.measureCd === dpMeasure.FF_AMT.cd || params.data.measureCd === dpMeasure.FF_QTY.cd) {
          const type = params.value.cd;
          if (isEmpty(disaggTargetObj.current)) {
            const row = {
              moduleCd: module.DP,
              activeFlg: true,
              configCd: DISAGGREGATION_TARGET,
              boolVal: false,
              strVal: type,
            };
            disaggTargetObj.current = row;
          } else {
            disaggTargetObj.current.strVal = type;
          }
          setNodeData(colId, params);
        } else if (includes(salesPlanMeasures, params.data.measureCd) || includes(financePlanMeasures, params.data.measureCd)) {
          const type = {
            measure: "salesPlan",
            target: params.value.cd
          };
          if (isEmpty(disaggTargetObj.current)) {
            const row = {
              moduleCd: module.DP,
              activeFlg: true,
              configCd: DISAGGREGATION_TARGET,
              boolVal: false,
              objVal: type,
            };
            disaggTargetObj.current = row;
          } else {
            disaggTargetObj.current.objVal = type;
          }
          setNodeData(colId, params);
        } else if (includes(avgMeasures, params.data.measureCd)) {
          const period = params.value;
          if (period) {
            const n = parseInt(period?.cd, 10);
            setAvgMonths(n);
          } else {
            setAvgMonths(3);
          }
          setNodeData(colId, params);
        } else if (includes(posAvgMeasures, params.data.measureCd)) {
          const period = params.value;
          if (period) {
            const n = parseInt(period?.cd, 10);
            setPosAvgMonths(n);
          } else {
            setPosAvgMonths(3);
          }
          setNodeData(colId, params);
        }
      } else if (isEqual(colId, "disaggRuleCd")) {
        setNodeData(colId, params);
      } else {
        DefaultGridSetting({}).onCellValueChanged(params);
      }
    }

    /*
     * 왼쪽 목록에서 바꾼 값을 오른쪽 패널에도 비친다.
     * 행 클릭 때 뜬 사본으로는 부족하다 — AG Grid 는 colDef.onCellClicked(ON 스위치·ƒ 배지 토글)를
     * setTimeout(0) 으로 미뤄 부르므로 onRowClicked(사본 생성)보다 늦게 돈다.
     * 그래서 값이 실제로 바뀌는 이 시점에 사본을 다시 뜬다(전체 켜기/끄기도 같은 경로).
     */
    if (params.node && params.node === selectedNodeRef.current) {
      setSelectedRowSnapshot({ ...params.node.data });
    }

    refreshMeasureCounts(params.api);
  };

  const changeDtf = value => {
    const newValue = parseInt(value, 10);
    if (newValue < 0) {
      setDtfError(true);
      setDTF(0);
    } else {
      setDtfError(false);
      setDTF(newValue);
    }
  };

  const changeRolling = value => {
    const newValue = parseInt(value, 10);
    if (newValue < 0) {
      setRollingError(true);
      setRollingRange(0);
    } else {
      setRollingError(false);
      setRollingRange(newValue);
    }
  };

  const addRow = () => {
    const newRow = {
      id: null,
      seqNum: null,
      measureCd: null,
      computeFlg: false,
      name: null,
      valueTypeCd: codeUtil.dpMeasureType.AMT,
      formulaExpValue: null,
      formulaExp: null,
      aggRuleCd: codeUtil.dpAggRules.SUM,
      disaggRuleCd: codeUtil.dpDisAggRules.PROPORTION,
      disaggUnitFlg: false,
      activeFlg: true,
      owner: null,
      descTxt: null,
      simFlg: false,
    };

    // 네 번째 인자(selected)를 끈다 — 선택 표시가 남으면 체크박스 열이 다시 나타난다.
    const createdNode = GridUtils.addRow(gridApi, newRow, 0, false);
    DataState.setDataState(createdNode);
    // 새 measure 는 바로 오른쪽 패널에서 채워 넣게 한다(빈 줄만 생기고 어디 쓰는지 모르는 상태를 피한다).
    pickMeasureNode(createdNode);
    return true;
  };

  const inputBoxStyle = {
    width: "100%",
    mb: 2,
    mt: 1.5,
    minWidth: "800px"
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const clickInitialDetail = params => {
    if (params.type === "click") {
      setInitialSettingOpen(true);
    }
  };


  const handleAlertSettings = () => {
    setAlertSetOpen(!alertSetOpen);
  };

  const retrieveUserRoles = () => {
    userService
      .getUserAccessRoles({ username: userInfo.username })
      .then(response => {
        const userAccess = response.data;
        setUserRoles(userAccess?.roles);
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveDpDisplayNameMeasureList = () => {
    measureService.getDisplayNameList()
      .then(response => {
        const measures = response.data ?? [];
        // 표시명 저장 키(displayNames)는 name(번역 키) 기준이라 measures 는 그대로 이름 목록으로 두고,
        // 채널 전용 measure 판단(measureCd 필요)을 위해 원본도 따로 들고 있는다.
        setDpDisplayNameMeasuresRaw(measures);
        setDpDisplayNameMeasureList(measures.map(measure => measure.measure));
        setDpMeasureValueTypeMap(
          measures.reduce((acc, measure) => {
            acc[measure.measure] = measure.valueTypeCd;
            return acc;
          }, {})
        );
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveMeasureDisplayNames = () => {
    planConfigService.getMeasureDisplayNames(codeUtil.module.DP)
      .then(response => {
        const { data } = response;
        if (!isEmpty(data)) {
          measureDisplayNameConfig.current = data;
        }
        setMeasureDisplayNames(data?.objVal ?? {});
      })
      .catch(e => {
        debug(e);
      });
  };

  const saveMeasureDisplayNames = () => {
    planConfigService.upsert([{ ...measureDisplayNameConfig.current, objVal: measureDisplayNames }])
      .then(() => {
        retrieveMeasureDisplayNames();
      })
      .catch(e => {
        debug(e);
      });
  };

  useEffect(() => {
    if (isSetConfig) {
      retrieveMeasures();
      setIsSetConfig(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSetConfig]);

  useEffect(() => {
    retrieveUserRoles();
    retrieveDpDisplayNameMeasureList();
    retrieveMeasureDisplayNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isEmpty(userRoles)) {
      retrieveHrchyConfig();
      initialPlanConfig();
      retrievePlanConfig();
      retrievePlanVersion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles]);

  useEffect(() => {
    if (!isEmpty(itemLevelCds)) {
      initialPlanConfig();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemLevelCds]);

  useEffect(() => {
    if (measureData.length > 0) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: measureColumnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureData]);

  useEffect(() => {
    if (gridApi) {
      gridApi.forEachNode(node => {
        const rowNodeData = node.data;
        if (rowNodeData.activeFlg !== selectAll) {
          rowNodeData.activeFlg = selectAll;
          node.setDataValue("activeFlg", selectAll);
          const updateRow = gridApi.applyTransaction({ update: [rowNodeData] }).update[0];
          DataState.setDataState(updateRow);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  useEffect(() => {
    if (oriMeasureList.length > 0) {
      const initialMeasures = codeUtil.getSourceMeasureList(oriMeasureList, defaultValue, posActualFlg);
      setInitialMeasureList(initialMeasures);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue, oriMeasureList, posActualFlg]);

  return (
    <Box id={"data-load-validation"} sx={{mt: 1, height: "100%", width: "100%"}}>
      <Box sx={{width: "100%", height: "calc(100% - 8px)", overflow: "auto"}}>
        {/* Version Setting */}
        <Box sx={{width: "100%", height: "auto", mb: 2}}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMore/>} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("version")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="content-main">
                <Box sx={{width: "100%"}}>
                  <Box sx={{width: "50%"}}>
                    <Typography
                      variant="h6"
                      sx={{mb: 2}}>
                      {t("planningBucket")}
                    </Typography>
                    <Box sx={inputBoxStyle}>
                      <FormControl sx={{width: "30%"}}>
                        <InputLabel>{t("planningBucket")}</InputLabel>
                        <Select
                          size="small"
                          variant="outlined"
                          name="bucketUnit"
                          label={t("planningBucket")}
                          value={bucketUnit}
                          onChange={event => {
                            handleChangeSelect(event);
                          }}>
                          {timeDimensions.current.length > 0 &&
                            timeDimensions.current.map(level => {
                              return (
                                <MenuItem
                                  value={level}
                                  key={level}>
                                  {t(level)}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl>
                      {codeUtil.isWeekBucket(bucketUnit) ? (
                        <FormControl sx={{width: "30%", ml: "1%"}}>
                          <InputLabel>{t("dayOfWeek")}</InputLabel>
                          <Select
                            size="small"
                            variant="outlined"
                            name="startDay"
                            label={t("dayOfWeek")}
                            value={startDay}
                            onChange={event => {
                              handleChangeSelect(event);
                            }}
                            disabled={!codeUtil.isWeekBucket(bucketUnit)}>
                            {values(dayOfWeek).length > 0 &&
                              values(dayOfWeek).map(day => {
                                return (
                                  <MenuItem
                                    value={day.cd}
                                    disabled={day.disabled}
                                    key={t(day.cd)}>
                                    {t(day.cd)}
                                  </MenuItem>
                                );
                              })}
                          </Select>
                        </FormControl>
                      ) : null}
                    </Box>
                  </Box>
                  <Box sx={{width: "50%"}}>
                    <Typography
                      variant="h6"
                      sx={{mb: 2}}>
                      {t("planHorizon")}
                    </Typography>
                    <Box sx={inputBoxStyle}
                         size="small">
                      <FormControl
                        size="small"
                        sx={{width: "30%"}}>
                        <TextField
                          label={t("planRange")}
                          size="small"
                          value={planRange}
                          name="planRange"
                          type="number"
                          inputProps={{inputMode: "numeric", pattern: "[0-9]*", min: 1}}
                          error={isEqual(planRange, "") || planRange < 1}
                          sx={{textAlign: "center"}}
                          onChange={event => {
                            const newValue = parseInt(event.target.value, 10);
                            if (isNaN(newValue) || newValue <= 0) {
                              return;
                            }
                            setPlanRange(newValue);
                          }}
                        />
                      </FormControl>
                      <FormControl sx={{width: "30%", ml: "1%"}}>
                        <TextField
                          size="small"
                          label={t("dtf")}
                          value={dtf}
                          type="number"
                          inputProps={{inputMode: "numeric", pattern: "[0-9]+"}}
                          sx={{textAlign: "center"}}
                          error={dtfError}
                          onChange={e => {
                            changeDtf(e.target.value);
                          }}
                        />
                      </FormControl>
                    </Box>
                  </Box>
                  <Box>
                    <Typography
                      variant="h6"
                      sx={{mb: 2}}>
                      {t("createDefaultValue")}
                    </Typography>
                    <Box sx={{mt: 1.5, mb: 2}}>
                      <FormControl sx={{width: "50%", minWidth: "800px"}}>
                        <InputLabel>{t("default")}</InputLabel>
                        <Box sx={{display: "flex"}}>
                          <Select
                            sx={{width: "30%", mr: "1%"}}
                            size="small"
                            variant="outlined"
                            id="defaultValue"
                            name="defaultValue"
                            value={defaultValue}
                            label={t("default")}
                            onChange={event => {
                              handleChangeSelect(event);
                            }}>
                            {initialValues?.map(initalValue => {
                              return (
                                <MenuItem
                                  key={initalValue}
                                  value={initalValue}>
                                  {t(initalValue)}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {defaultValue !== CUSTOMIZATION ? (
                            <CalculationButton
                              label="computeExpressSetting"
                              color={computeMeasureList.length > 0 ? "primary" : "black"}
                              disabled={defaultValue === NOTINITIAL}
                              onClick={filterActionItem}
                            />
                          ) : null}
                          {defaultValue === CUSTOMIZATION ? (
                            <DetailButton
                              label="initialPlanDetailSetting"
                              color={"primary"}
                              onClick={clickInitialDetail}
                            />
                          ) : null}
                        </Box>
                        {defaultValue === BASELINE_FORECAST || (defaultValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList))? (
                          <Box sx={{mt: 1, width: "100%", display: "flex"}}>
                            <FormControl sx={{width: "30%", mr: "1%"}}>
                              <InputLabel>{t(CUST_LEVEL)}</InputLabel>
                              <Select
                                id={CUST_LEVEL}
                                name={CUST_LEVEL}
                                value={!isEmpty(custLevelCd) ? custLevelCd?.id : custLevelCds ? custLevelCds[0]?.id : CUSTOMER.id}
                                label={t(CUST_LEVEL)}
                                size="small"
                                variant="outlined"
                                onChange={event => {
                                  handleChangeSelect(event);
                                }}>
                                {custLevelCds?.map(custLevel => {
                                  return (
                                    <MenuItem
                                      key={custLevel.id}
                                      value={custLevel.id}>
                                      {custLevel.name}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                            <FormControl sx={{width: "30%"}}>
                              <InputLabel>{t(ITEM_LEVEL)}</InputLabel>
                              <Select
                                id={ITEM_LEVEL}
                                name={ITEM_LEVEL}
                                value={!isEmpty(itemLevelCd) ? itemLevelCd?.id : itemLevelCds ? itemLevelCds[0]?.id : ITEM.id}
                                label={t(ITEM_LEVEL)}
                                size="small"
                                variant="outlined"
                                onChange={event => {
                                  handleChangeSelect(event);
                                }}>
                                {itemLevelCds?.map(itemLevel => {
                                  return (
                                    <MenuItem
                                      key={itemLevel.id}
                                      value={itemLevel.id}>
                                      {itemLevel.name}
                                    </MenuItem>
                                  );
                                })}
                              </Select>
                            </FormControl>
                          </Box>
                        ) : (
                          <Box></Box>
                        )}
                        <FormControl
                          size="small"
                          sx={{width: "30%", mt: 1}}>
                          <TextField
                            label={t("rollingRange")}
                            size="small"
                            value={rollingRange}
                            name="rollingRange"
                            type="number"
                            inputProps={{inputMode: "numeric", pattern: "[0-9]+"}}
                            sx={{textAlign: "center"}}
                            error={rollingError}
                            onChange={e => {
                              changeRolling(e.target.value);
                            }}
                          />
                        </FormControl>
                        {includes(userRoles, roles.APP_BF) ? (
                          <FormControlLabel
                            sx={{ ml: 0, mt: 1 }}
                            control={
                              <StyledSwitch
                                name="runBf"
                                checked={runBf}
                                onChange={() => {
                                  setRunBf(!runBf);
                                }}
                                sx={{ mr: 2 }}
                              />
                            }
                            label={t("runBf")}
                          />
                        ) : null}
                      </FormControl>
                    </Box>
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="h6" sx={{mb: 2}}>{t("noteCopy")}
                    </Typography>
                    <Box sx={{width: "80%", ml: 2}}>
                      <FormControlLabel
                        control={<StyledSwitch name={"noteFlg"} checked={noteFlg}
                                           onChange={param => handleChangeSelect(param)} sx={{ mr: 2 }}/>}
                        label={t("lastReleasedNote")} key="NOTE_FLG" sx={{display: "flex", ml: 0}}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="h6" sx={{mb: 2}}>{t("dpNetting")}
                    </Typography>
                    <Box sx={{width: "100%", ml: 2}}>
                      <FormControlLabel
                        sx={{width: "99%", ml: 0}}
                        control={
                          <StyledSwitch
                            name="dpNetting"
                            checked={isDpNetting}
                            onChange={() => {
                              setIsDpNetting(!isDpNetting);
                            }}
                            sx={{ mr: 2 }}
                          />
                        }
                        label={t("MSG_WhenRelesedDPReplacedSalesOrder")}
                      />
                    </Box>
                    {isDpNetting ? (
                        <SegmentedControl
                          sx={{ ml: 6, mb: 2 }}
                          value={dpNettingUnit}
                          onChange={(v) => setDpNettingUnit(v)}
                          options={[
                            { value: QTY, label: t("qty") },
                            { value: AMT, label: t("amt") },
                          ]}
                        />)
                      : null}
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="h6" sx={{mb: 2}}>{t("noOfMonthsSettings")}
                    </Typography>
                    <Box sx={{width: "100%", ml: 2}}>
                      <FormControlLabel
                        sx={{width: "99%", ml: 0}}
                        control={
                          <StyledSwitch
                            name="avgFlg"
                            checked={avgFlg}
                            onChange={() => {
                              setAvgFlg(!avgFlg);
                            }}
                            sx={{ mr: 2 }}
                          />
                        }
                        label={t("avgNoOfMonths", { N: avgMonths, measureName: t("avgSalesActual") })}
                      />
                      {/* BF Source Data(BF Settings로 옮긴 토글)가 꺼져 있으면 POS 계열을 안 쓰기로 한 것이므로 감춘다 */}
                      {posActualFlg && (
                        <FormControlLabel
                          sx={{width: "99%", ml: 0}}
                          control={
                            <StyledSwitch
                              name="posAvgFlg"
                              checked={posAvgFlg}
                              onChange={() => {
                                setPosAvgFlg(!posAvgFlg);
                              }}
                              sx={{ mr: 2 }}
                            />
                          }
                          label={t("avgNoOfMonths", { N: posAvgMonths, measureName: t("posAvgSalesActual") })}
                        />
                      )}
                    </Box>
                  </Box>
                  {/*<FormControlLabel*/}
                  {/*  sx={{ width: "200%" }}*/}
                  {/*  control={*/}
                  {/*    <Checkbox*/}
                  {/*      name="applyApplySnapshot"*/}
                  {/*      checked={applySnapshot}*/}
                  {/*      onChange={() => {*/}
                  {/*        setApplySnapshot(!applySnapshot);*/}
                  {/*      }}*/}
                  {/*    />*/}
                  {/*  }*/}
                  {/*  label={t("applySnapshotToReleasedVersion")}*/}
                  {/*/>*/}

                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
        {/*Measure Setting*/}
        <Box sx={{width: "100%", height: "auto", mb: 2}}>
          <Accordion>
            {/* Accordion Title Area */}
            <AccordionSummary expandIcon={<ExpandMore/>} aria-controls="panel1a-content" id="panel1a-header">
              <Typography className="content-title">{t("workbenchMeasure")}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box className="content-main" sx={{ width: "100%", maxWidth: 1720 }}>
                {/* 페이지 오른쪽 끝까지 늘리지 않는다 — 목록 + 패널이 들어가는 폭까지만. */}
                <AppCard sx={{width: "100%", maxWidth: 1720}}>
                  {/* 헤더 — 개수 요약 + 검색/Active only/알림설정/추가·삭제 */}
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1,
                      px: 1.5, py: 1.25, borderBottom: 1, borderColor: "divider",
                      background: theme =>
                        `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0)} 45%)`,
                    }}
                  >
                    <StatPill label={t("measure")} value={measureCounts.total}/>
                    <StatPill label={t("active")} value={measureCounts.active} accent/>

                    <Box sx={{flexGrow: 1}}/>

                    <TextField
                      size="small"
                      variant="outlined"
                      value={measureKeyword}
                      placeholder={t("search")}
                      onChange={e => {
                        setMeasureKeyword(e.target.value);
                        applyMeasureFilter({keyword: e.target.value});
                      }}
                      sx={{
                        width: 190,
                        "& .MuiInputBase-root": {height: 30, fontSize: FS.SUB, borderRadius: 999, bgcolor: "background.paper"},
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{fontSize: 16, color: "text.disabled"}}/>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <SegmentedControl
                      value={activeOnly ? ACTIVE_ONLY : ALL_MEASURES}
                      onChange={v => {
                        const onlyActive = v === ACTIVE_ONLY;
                        setActiveOnly(onlyActive);
                        applyMeasureFilter({activeOnly: onlyActive});
                      }}
                      options={[
                        {value: ALL_MEASURES, label: t("all")},
                        {value: ACTIVE_ONLY, label: t("active")},
                      ]}
                    />
                    {/* 툴바 컨트롤은 검색창·세그먼트와 같은 알약 모양으로 맞춘다 */}
                    <Button
                      variant="outlined"
                      startIcon={<SettingsIcon/>}
                      size="small"
                      onClick={handleAlertSettings}
                      sx={TOOLBAR_BUTTON_SX}>
                      {t("alertSettings")}
                    </Button>
                    <Button
                      variant="contained"
                      disableElevation
                      startIcon={<AddIcon/>}
                      size="small"
                      onClick={addRow}
                      sx={TOOLBAR_BUTTON_SX}>
                      {t("addMeasure")}
                    </Button>
                    {/* 저장 전 편집을 되돌린다 — 서버에서 다시 읽어 변경행 추적도 초기화 */}
                    <Tooltip title={t("MSG_ResetMeasureEdits")}>
                      <Button
                        size="small"
                        startIcon={<ResetIcon/>}
                        onClick={handleResetMeasures}
                        sx={TOOLBAR_BUTTON_SX}>
                        {t("reset")}
                      </Button>
                    </Tooltip>
                  </Box>

                  {/* 왼쪽 목록 + 오른쪽 편집 패널 */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        md: `minmax(${MEASURE_GRID_MIN_WIDTH}px, 1080px) minmax(${MEASURE_DETAIL_MIN_WIDTH}px, 1fr)`,
                      },
                      height: 520,
                    }}
                  >
                    <Box
                      className="ag-theme-balham measure-sheet-grid"
                      sx={{height: "100%", borderRight: {md: 1}, borderColor: {md: "divider"}}}
                    >
                      <AgGridReact
                        ref={gridRef}
                        rowData={measureData}
                        {...defaultGridMemo}
                        /*
                         * 헤더의 컬럼 메뉴(≡) 버튼은 끈다 — 이 그리드는 표가 아니라 목록이라
                         * 저장된 컬럼 상태(p13n)를 복원하지 않고(onGridReady 참고) 속성 컬럼도 전부 숨겨 두었다.
                         * 메뉴를 열어도 할 수 있는 일이 없어 아이콘만 남는다.
                         */
                        defaultColDef={{...defaultGridMemo.defaultColDef, wrapHeaderText: false, autoHeaderHeight: false, suppressHeaderMenuButton: true}}
                        rowHeight={28}
                        headerHeight={28}
                        isExternalFilterPresent={isMeasureFilterPresent}
                        doesExternalFilterPass={doesMeasureFilterPass}
                        onModelUpdated={params => refreshMeasureCounts(params.api)}
                        onCellValueChanged={onCellValueChanged}
                        onGridReady={onGridReady}
                        onRowClicked={params => pickMeasureNode(params.node)}
                        rowClassRules={{"measure-row-picked": params => params.node?.id === selectedNodeId}}
                        rowSelection={{ mode: 'multiRow',
                          enableClickSelection: false,
                          checkboxes: false,
                          headerCheckbox: false }}
                        /* 선택 UI 는 쓰지 않는다 — 숨기지 않으면 AG Grid 가 체크박스 열을 스스로 끼워 넣는다 */
                        selectionColumnDef={{ hide: true }}
                        onCellKeyDown={onCellKeyDown}
                        tooltipShowDelay={100}
                        tooltipHideDelay={5000}
                      />
                    </Box>

                    <MeasureDetailPanel
                      selected={!!selectedRow}
                      instanceKey={selectedNodeId}
                      title={selectedRow ? GridUtils.measureLabel(selectedRow.name, dpMeasure[selectedRow.measureCd]?.name) : ""}
                      placeholderTitle={t("addMeasure")}
                      empty={t("MSG_SelectMeasureToEdit")}
                      badges={selectedRow ? [
                        ...(selectedRow.computeFlg ? [{label: "f", kind: "fx"}] : []),
                        ...(selectedRow.valueTypeCd ? [{label: GridUtils.gridValueL10N(selectedRow.valueTypeCd), kind: "chip"}] : []),
                      ] : []}
                      toggles={selectedRow ? [
                        {key: "activeFlg", label: t("activeFlg"), checked: selectedRow.activeFlg},
                        {
                          key: "computeFlg", label: t("compute"), checked: selectedRow.computeFlg,
                          disabled: includes(dpBasicMeasureCds, selectedRow.measureCd),
                        },
                      ] : []}
                      fields={buildPanelFields()}
                      onChange={setSelectedValue}
                      onDelete={selectedRow && !includes(dpBasicMeasureCds, selectedRow.measureCd)
                        ? () => setDialogInfo({open: true, title: "delete", content: t("MSG_DeleteConfirm")})
                        : undefined}
                      deleteLabel={t("delete")}
                    />
                  </Box>

                  {/* 푸터 — 표시 건수 + ƒ 범례 */}
                  <Box
                    sx={{
                      display: "flex", alignItems: "center", gap: 1,
                      px: 1.5, py: 0.75, borderTop: 1, borderColor: "divider", bgcolor: "grey.50",
                      fontSize: FS.BODY, color: "text.disabled",
                    }}
                  >
                    <Box component="span">{`${measureCounts.shown} / ${measureCounts.total}`}</Box>
                    <Box component="span">·</Box>
                    <Box component="span">{`ƒ = ${t("compute")}`}</Box>
                  </Box>
                </AppCard>
              </Box>

              <Box className="content-main" sx={{ width: "100%", maxWidth: 1720, mt: 3 }}>
                <MeasureNameSettings
                  measures={dpDisplayNameMeasures}
                  displayNames={measureDisplayNames}
                  onChange={setMeasureDisplayNames}
                  valueTypeByMeasure={dpMeasureValueTypeMap}
                  sx={MEASURE_NAME_SETTINGS_SX}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
        <Button sx={{width: "160px", height: "40px"}}
                className="content-button"
                color="primary"
                variant="contained"
                component="span"
                startIcon={<SaveIcon/>}
                onClick={handleSave}>
          {t("save")}
        </Button>
      </Box>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        el={dialogInfo.el}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Popover
        id={popoverId}
        open={popoverOpen}
        anchorEl={popoverEl}
        onClose={popoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        <Typography sx={{ p: 1.2, fontSize: FS.H2 }}>
          {t("MSG_EditableFormulaExplanation")}
          <br></br>
          {t("availableCals")}:<strong> + - * / </strong>
          <br></br>
          {t("MSG_FormulaExplanation")} <br></br>
          ex&#41; &#91;MEASURE&#93;* 1.1
        </Typography>
      </Popover>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "outlined-basic",
        }}
        anchorEl={menuEl}
        open={menuOpen}
        onClose={menuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: "20ch",
          },
        }}>
        {oriMeasureList?.map(computeMeasure => {
          return (
            <MenuItem
              onClick={() => addExpression(computeMeasure.name)}
              value={computeMeasure.name}
              key={computeMeasure.name}>
              {GridUtils.gridValueL10N(computeMeasure.name)}
            </MenuItem>
          );
        })}
      </Menu>
      <ComputeMeasureModal
        open={filterModalOpen}
        close={handelFilterClose}
        modalResultData={setComputeMeasureList}
        target={initialTarget.length > 0 ? initialTarget : oriMeasureList}
        source={initialMeasureList.length > 0 ? initialMeasureList : oriMeasureList}
        computeList={computeMeasureList}
      />
      <DpMeasureAlertModal
        open={alertSetOpen}
        close={handleAlertSettings}
      />
      <DpInitialSettingModal
        open={initialSettingOpen}
        close={handelIntialSettingClose}
        itemLevelCds={itemLevelCds}
        custLevelCds={custLevelCds}
        initialValues={initialValues}
        oriMeasureList={oriMeasureList}
        configDatas={idMap?.[INITIAL_DEMAND]}
        modalResultData={settingInitialConfig}
        posActualFlg={posActualFlg}
      />
    </Box>
  );
};

export default withTranslation()(DpSettings);
